import { Box } from "@mui/material";
import { useMemo } from "react";
import { PulseFilter } from "../defs/PulseFilter";
import { RollGradient } from "../defs/RollGradient";
import { TempGradients } from "../defs/TempGradients";
import type { MillVisualizerData } from "../models/MillVisualizer";
import {
	PASS_LINE_Y,
	PROPERTY_COLUMN_WIDTH,
	ROUGHING_LAST_POSITION,
	STAGE_HEIGHT,
	STAND_SLOT_WIDTH,
	standLeftX,
} from "../utils/viewBoxLayout";
import { Billet } from "./Billet";
import { PassLine } from "./PassLine";
import { PreheatingFurnace } from "./PreheatingFurnace";
import { StandBlock } from "./StandBlock";

interface Props {
	data: MillVisualizerData;
	onStandClick?: (id: string) => void;
}

const GLOBAL_STYLES = `
	@keyframes visualizer-dash-flow {
		from { stroke-dashoffset: 0; }
		to { stroke-dashoffset: -20; }
	}
	@keyframes visualizer-pulse {
		0%, 100% {
			transform: scale(1);
			filter: drop-shadow(0 0 1px rgba(63, 81, 181, 0.25));
		}
		50% {
			transform: scale(1.18);
			filter: drop-shadow(0 0 8px rgba(63, 81, 181, 0.65));
		}
	}
	.visualizer-billet {
		transition: transform 500ms linear;
	}
	.visualizer-billet-shape {
		transition: x 500ms linear, y 500ms linear, width 500ms linear, height 500ms linear, rx 500ms linear, ry 500ms linear;
	}
	.visualizer-pass-flow {
		animation-name: visualizer-dash-flow;
		animation-timing-function: linear;
		animation-iteration-count: infinite;
	}
	.visualizer-pulse {
		animation: visualizer-pulse 1.4s ease-in-out infinite;
		transform-origin: 50% 50%;
	}
`;

// Section bands — colour only, no labels. Names like "ROUGHING" / "FINISHING"
// might not match Roanoke's terminology, so we communicate the split with a
// subtle background tint and let the operator's mental model fill in the rest.
const ROUGHING_BG = "rgba(59, 130, 246, 0.06)";
const FINISHING_BG = "rgba(34, 197, 94, 0.06)";

export const VisualizerStage = ({ data, onStandClick }: Props) => {
	const sortedStands = useMemo(
		() => [...data.stands].sort((a, b) => a.position - b.position),
		[data.stands],
	);

	const viewBoxWidth = sortedStands.length * STAND_SLOT_WIDTH;

	const totalStands = sortedStands.length;
	const roughingEnd = Math.min(ROUGHING_LAST_POSITION, totalStands);

	const roughingX = 0;
	const roughingW = roughingEnd * STAND_SLOT_WIDTH;
	const finishingX = roughingW;
	const finishingW = (totalStands - roughingEnd) * STAND_SLOT_WIDTH;

	const passLines = useMemo(() => {
		const arr: { from: number; to: number; speed: number }[] = [];
		for (let i = 0; i < sortedStands.length - 1; i++) {
			const a = sortedStands[i];
			const b = sortedStands[i + 1];
			arr.push({
				from: a.position,
				to: b.position,
				speed: ((a.speedMps ?? 0) + (b.speedMps ?? 0)) / 2,
			});
		}
		return arr;
	}, [sortedStands]);

	const activeBetween = useMemo(() => {
		const set = new Set<string>();
		for (const b of data.billets) {
			for (let p = b.firstStandPosition; p < b.lastStandPosition; p++) {
				set.add(`${p}-${p + 1}`);
			}
		}
		return set;
	}, [data.billets]);

	if (sortedStands.length === 0) return null;

	return (
		<Box
			sx={{
				position: "relative",
				pl: `${PROPERTY_COLUMN_WIDTH}px`,
				width: "100%",
			}}
		>
			<Box
				sx={{
					position: "absolute",
					left: 0,
					top: 0,
					bottom: 0,
					width: `${PROPERTY_COLUMN_WIDTH}px`,
					p: 0.25,
				}}
			>
				<PreheatingFurnace variant="stage" />
			</Box>
			<svg
				viewBox={`0 0 ${viewBoxWidth} ${STAGE_HEIGHT}`}
				preserveAspectRatio="xMidYMid meet"
				style={{ width: "100%", height: "auto", display: "block" }}
			>
				<defs>
					<style>{GLOBAL_STYLES}</style>
					<TempGradients />
					<RollGradient />
					<PulseFilter />
				</defs>

				{roughingW > 0 ? (
					<rect
						x={roughingX}
						y={0}
						width={roughingW}
						height={STAGE_HEIGHT}
						fill={ROUGHING_BG}
						pointerEvents="none"
					/>
				) : null}
				{finishingW > 0 ? (
					<rect
						x={finishingX}
						y={0}
						width={finishingW}
						height={STAGE_HEIGHT}
						fill={FINISHING_BG}
						pointerEvents="none"
					/>
				) : null}

				{/* Leading pass line — from the SVG's left edge to stand 1's left
			    edge. Visually continues the pass line out of the pre-heating
			    furnace's "mouth" (the HTML overlay sitting just to the left).
			    Same dashed style as the internal pass lines so the connection
			    reads naturally. */}
				<line
					x1={0}
					y1={PASS_LINE_Y}
					x2={standLeftX(1)}
					y2={PASS_LINE_Y}
					stroke="#9ca3af"
					strokeWidth={2}
					strokeDasharray="6 4"
				/>
				{passLines.map((line) => (
					<PassLine
						key={`line-${line.from}-${line.to}`}
						fromPosition={line.from}
						toPosition={line.to}
						speedMps={line.speed}
						active={activeBetween.has(`${line.from}-${line.to}`)}
					/>
				))}

				{sortedStands.map((s) => (
					<StandBlock key={s.id} stand={s} onClick={onStandClick} />
				))}

				{data.billets.map((b) => (
					<Billet key={b.id} billet={b} />
				))}
			</svg>
		</Box>
	);
};
